import type { BankOfChangesCopy } from './bankOfChangesCopy';
import { activitySummary, isRunning, makeInitialBankSession, versionTurn, withTurns, withWarnings } from './bankOfChangesFlow';
import { isStoredUpload } from './types';
import type {
  BankActivity,
  BankConversationInfo,
  BankServerMessage,
  BankSessionState,
  BankUpload,
  BankVersionFiles,
} from './types';

/**
 * The conversation is kept on the server, never in the pane.
 *
 * Until the first version exists the pane leads it: after every change it
 * sends the server a draft (the files and the transcript as they stand), and a
 * newly picked file is uploaded with it once. From the first version on the
 * server writes the conversation itself (the lawyer's message, the run, the
 * agent's reply, the files), so it goes on the same way from the dashboard.
 *
 * A run goes on in the server, and what it came to is the server's to write.
 * So a conversation read back while a run is going on is shown as running and
 * followed from there, and one read back after a run shows its outcome —
 * whether or not the pane was open meanwhile.
 *
 * Everything here is pure: what a draft holds, and how a conversation read
 * back from the server becomes the pane's session.
 */

/** The longest turn and the most turns a draft carries; the server refuses more. */
export const MAX_DRAFT_TURN_CHARS = 20000;
export const MAX_DRAFT_TURNS = 400;

/** The name of the database earlier versions kept conversations in, on the lawyer's computer. */
export const LEGACY_LOCAL_DATABASE = 'courtyard-bank-of-changes';

export type BankDraftFile = { file_id: string } | { filename: string; content_base64: string };

export type BankDraftTurn = {
  role: 'user' | 'assistant';
  content: string;
  activity?: BankActivity;
};

export type BankDraftPayload = {
  conversation_id?: string;
  bank: BankDraftFile | null;
  appendices: BankDraftFile[];
  turns: BankDraftTurn[];
};

/** A conversation is worth keeping once the lawyer put a file into it. */
export function worthSaving(state: BankSessionState): boolean {
  return state.bank !== null || state.appendices.length > 0;
}

/** The step the pane shows while a run of each kind is going on in the server. */
const RUNNING_STEP = { analyze: 'analyzing', build: 'building', revise: 'revising' } as const;

/** Whether the pane still leads the conversation: no version yet, and nothing running on the server. */
export function draftIsOpen(state: BankSessionState): boolean {
  return state.version === 0 && !isRunning(state);
}

/** What tells one draft from the next: the turns and the files. Two states with the same signature need no second save. */
export function draftSignature(state: BankSessionState): string {
  return [filesSignature(state), state.turns.length, state.turns[state.turns.length - 1]?.content.length ?? 0].join('|');
}

/** The files of a draft alone: a draft the server refused for one of them is not sent again until they change. */
export function filesSignature(state: BankSessionState): string {
  const describe = (file: BankUpload | null) => (file ? `${file.name.normalize('NFC')}/${file.size}` : '-');
  return [describe(state.bank), ...state.appendices.map(describe)].join(',');
}

/** The transcript as a draft carries it: text, and a run that ended (failed or stopped) with its stages. */
export function draftTurns(state: BankSessionState): BankDraftTurn[] {
  return state.turns.slice(-MAX_DRAFT_TURNS).map((turn) => {
    const sent: BankDraftTurn = { role: turn.role, content: turn.content.slice(0, MAX_DRAFT_TURN_CHARS) };
    if (turn.activity && turn.activity.state !== 'running') {
      sent.activity = { ...turn.activity, state: turn.activity.state };
    }
    return sent;
  });
}

/** One file of a draft: by its id when the server keeps it (or `known` says it does), otherwise uploaded. */
export async function draftFile(
  file: BankUpload,
  known: Map<BankUpload, string>,
  encode: (file: File) => Promise<string>,
): Promise<BankDraftFile> {
  if (isStoredUpload(file)) return { file_id: file.id };
  const id = known.get(file);
  return id ? { file_id: id } : { filename: file.name, content_base64: await encode(file) };
}

export async function draftPayload(
  state: BankSessionState,
  known: Map<BankUpload, string>,
  encode: (file: File) => Promise<string>,
): Promise<BankDraftPayload> {
  const payload: BankDraftPayload = {
    bank: state.bank ? await draftFile(state.bank, known, encode) : null,
    appendices: await Promise.all(state.appendices.map((file) => draftFile(file, known, encode))),
    turns: draftTurns(state),
  };
  if (state.conversationId) payload.conversation_id = state.conversationId;
  return payload;
}

function versionFiles(metadata: NonNullable<BankServerMessage['metadata']>): BankVersionFiles | null {
  // A version is a built file the server names, of version 1 or later; anything else is shown as text.
  const built = metadata.files?.find((file) => file.kind === 'built' && file.id);
  const version = metadata.version ?? built?.version ?? 0;
  if (!built || version < 1) return null;
  const refined = metadata.files?.find((file) => file.kind === 'refined' && file.id) ?? null;
  return {
    version,
    built: { id: built.id, filename: built.filename, size: built.size },
    refined: refined ? { id: refined.id, filename: refined.filename, size: refined.size } : null,
  };
}

/**
 * A conversation read back from the server, as the pane's session. A turn that
 * is more than text (a run, a version's files, a notice that a run stopped or
 * failed) is drawn in the pane's own words from what the server kept of it.
 */
export function sessionFromServer(
  conversation: BankConversationInfo,
  messages: BankServerMessage[],
  copy: BankOfChangesCopy,
): BankSessionState {
  const run = conversation.run;
  let state: BankSessionState = {
    ...makeInitialBankSession(copy),
    turns: [],
    step: run ? RUNNING_STEP[run.kind] : conversation.step,
    conversationId: conversation.id,
    bank: conversation.bank,
    appendices: conversation.appendices,
    analysis: conversation.analysis,
    version: conversation.version,
    answerable: conversation.answerable,
    // A run that is going on in the server is followed from where it stands.
    following: run !== null,
    stopping: Boolean(run?.stop_requested),
    activity:
      run && run.kind !== 'analyze'
        ? { ...(run.activity ?? { kind: run.kind, clusters: [] }), state: 'running' }
        : null,
  };
  for (const message of messages) {
    const metadata = message.metadata ?? {};
    const files = metadata.kind === 'files' ? versionFiles(metadata) : null;
    if (metadata.kind === 'activity' && metadata.activity) {
      const activity = metadata.activity;
      state = withTurns(state, { role: 'assistant', content: activitySummary(activity, copy), activity });
    } else if (files) {
      state = withTurns(state, versionTurn(files, copy));
    } else if (metadata.kind === 'notice' && metadata.notice === 'failed') {
      const next =
        metadata.run === 'analyze' ? copy.afterAnalyzeError : metadata.run === 'build' ? copy.afterBuildError : copy.afterReviseError;
      state = withTurns(state, { role: 'assistant', content: `${message.content}\n\n${next}` });
    } else {
      const content = message.is_user ? message.content : withWarnings(message.content, metadata.warnings, copy.warningsHeading);
      state = withTurns(state, { role: message.is_user ? 'user' : 'assistant', content });
    }
  }
  if (state.turns.length === 0) return { ...state, turns: makeInitialBankSession(copy).turns };
  // A bank that was analysed waits for the lawyer's word to build; the pane asks for it, once.
  const asked = copy.confirmPrompt(state.appendices.length);
  if (state.step === 'awaiting_confirmation' && state.turns[state.turns.length - 1].content !== asked) {
    state = withTurns(state, { role: 'assistant', content: asked });
  }
  return state;
}

/**
 * Earlier versions kept conversations in the pane's own database, on the
 * lawyer's computer. Nothing is kept there any more, and what was is deleted.
 */
export function forgetLocalConversations(factory: IDBFactory | undefined = globalThis.indexedDB): void {
  try {
    factory?.deleteDatabase(LEGACY_LOCAL_DATABASE);
  } catch {
    /* no database in this pane: nothing was kept */
  }
}
